import { useEffect, useMemo, useState } from 'react';
import { callable } from '../lib/callable';
import { MentorAvatarImg } from './MentorAvatarImg';

// Slim auto-scrolling "ticker" of published success stories on the homepage. Fed by the
// existing listSuccessStories callable (no backend work). Seamless infinite loop = the list
// duplicated once + a CSS translateX(0 → -50%). Pauses on hover; for reduced-motion users it
// falls back to a plain manual overflow-scroll (never auto-moves). Renders nothing if empty.
interface Story { displayName: string; slug: string; profilePhotoUrl: string | null; examsTaught: string[]; city: string | null; title: string; body: string }

function initials(name: string): string {
  return name.split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

export function SuccessStoriesTicker() {
  const [stories, setStories] = useState<Story[]>([]);
  useEffect(() => {
    callable<unknown, { stories: Story[] }>('listSuccessStories', {})
      .then((r) => setStories((r.data.stories ?? []).slice(0, 15)))
      .catch(() => setStories([]));
  }, []);

  // ~3s per card so a short list doesn't whip past; floor at 20s.
  const duration = useMemo(() => Math.max(20, stories.length * 3), [stories.length]);
  if (stories.length === 0) return null;
  const loop = [...stories, ...stories];

  return (
    <section style={{ padding: '1.1rem 1.25rem', background: 'white', border: '1px solid #ddd', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1.5rem' }}>
      <style>{`
        .sst { overflow: hidden; }
        .sst-track { display: flex; gap: 0.75rem; width: max-content; }
        @media (prefers-reduced-motion: no-preference) {
          .sst-track { animation: sst-scroll ${duration}s linear infinite; }
          .sst:hover .sst-track { animation-play-state: paused; }
        }
        @media (prefers-reduced-motion: reduce) {
          .sst { overflow-x: auto; scrollbar-width: none; }
          .sst::-webkit-scrollbar { display: none; }
        }
        @keyframes sst-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <h2 style={{ margin: 0, color: '#1a4480', fontSize: '1.1rem' }}>🌟 Success Stories</h2>
        <a href="/success-stories" style={{ color: '#1a4480', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none' }}>See all →</a>
      </div>
      <div className="sst">
        <div className="sst-track">
          {loop.map((s, i) => (
            <a key={i} href={`/m/${s.slug}`} aria-hidden={i >= stories.length ? true : undefined}
              style={{ flex: '0 0 auto', width: 'min(300px, 82vw)', display: 'flex', gap: '0.6rem', alignItems: 'center', padding: '0.6rem 0.75rem', border: '1px solid #eef2f7', borderRadius: 10, background: '#fafbfc', textDecoration: 'none', color: 'inherit' }}>
              {s.profilePhotoUrl
                ? <MentorAvatarImg src={s.profilePhotoUrl} alt={s.displayName} px={40} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#eee' }} />
                : <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#1a4480', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem', flexShrink: 0 }}>{initials(s.displayName)}</div>}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#15294d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.displayName}{s.city ? ` · ${s.city}` : ''}</div>
                <div style={{ fontSize: '0.72rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.body.slice(0, 60)}{s.body.length > 60 ? '…' : ''}</div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
